/**
 * Cost Services — the bill side (ALC-4476, F1-S3): line destination, candidate provisions,
 * pending selections and settlement matches. Shapes shared by the API, the MCP and the web.
 *
 * Money is a decimal string everywhere. Amounts of a line, a selection and a match are in the
 * bill's document currency, which must be the provision's (vendor) currency; functional amounts
 * say so in their name.
 */

import type { ApBillLineDestination, CostMatchSource } from '../generated/enums.js';

// `ApBillLineDestination` (what a bill line's amount does on approval; COST_POOL is reserved for
// Phase 3) and `CostMatchSource` come from the Prisma enums (`generated/enums.ts`).

/** The destinations Phase 1 accepts; anything else is COST_LINE_DESTINATION_NOT_AVAILABLE. */
export const AVAILABLE_AP_BILL_LINE_DESTINATIONS = [
  'EXPENSE',
  'COST_PROVISION',
] as const satisfies readonly ApBillLineDestination[];
export type AvailableApBillLineDestination = (typeof AVAILABLE_AP_BILL_LINE_DESTINATIONS)[number];

export function isAvailableApBillLineDestination(
  value: unknown,
): value is AvailableApBillLineDestination {
  return (
    typeof value === 'string' &&
    (AVAILABLE_AP_BILL_LINE_DESTINATIONS as readonly string[]).includes(value)
  );
}

/**
 * Who set the destination: `USER`, `MCP`, `UNDO` (an undone match puts the line back to expense),
 * `INTAKE_RULE:<intakeRuleId>`, `COST_SERVICE_FLAG:<costServiceId>` (D27: intake stamped it
 * because a cost service of the bill's vendor has "Marcar todas as contas a pagar desse
 * fornecedor como custo de serviço automaticamente" on) or `VENDOR_PRECEDENT:<sourceBillId>`
 * (D18, retired by D27: kept readable for bills stamped before).
 */
export type ApBillLineDestinationSource =
  | 'USER'
  | 'MCP'
  | 'UNDO'
  | `INTAKE_RULE:${string}`
  | `COST_SERVICE_FLAG:${string}`
  | `VENDOR_PRECEDENT:${string}`;

/** D27: the line destination source prefix of a cost service's per-vendor flag. */
export const COST_SERVICE_FLAG_PREFIX = 'COST_SERVICE_FLAG:';

/** D27: the cost service id of a `COST_SERVICE_FLAG:<id>` source; null for any other source. */
export function costServiceIdOfFlagSource(source: string | null | undefined): string | null {
  if (!source?.startsWith(COST_SERVICE_FLAG_PREFIX)) return null;
  const id = source.slice(COST_SERVICE_FLAG_PREFIX.length);
  return id === '' ? null : id;
}

export function intakeRuleDestinationSource(intakeRuleId: string): ApBillLineDestinationSource {
  return `INTAKE_RULE:${intakeRuleId}`;
}

/** D27: the source of a line stamped COST_PROVISION by a cost service's per-vendor flag. */
export function costServiceFlagDestinationSource(
  costServiceId: string,
): ApBillLineDestinationSource {
  return `COST_SERVICE_FLAG:${costServiceId}`;
}

/** D27: the bill-history note of a line destination stamped by a cost service's vendor flag. */
export function costServiceFlagDestinationNote(costServiceName: string): string {
  return `Marcado automaticamente (serviço de custo «${costServiceName}»)`;
}

/**
 * Bill statuses in which a line's destination and selection may change. A rejected bill goes
 * back to the analyst and re-enters approval through the same readiness pre-flight.
 */
export const COST_LINE_EDITABLE_BILL_STATUSES = ['DRAFT', 'REJECTED'] as const;

export function isCostLineEditableBillStatus(status: string): boolean {
  return (COST_LINE_EDITABLE_BILL_STATUSES as readonly string[]).includes(status);
}

/**
 * D48: `generatedBy` of the one line a draft with no lines gets for its whole amount when its
 * vendor has an ACTIVE cost service in the bill's entity (a bill typed by hand has no line, so the
 * "Baixa de provisão" section had nothing to act on). The line follows the bill's total, currency
 * and vendor while the bill is a draft and is removed when a real line arrives.
 */
export const AP_BILL_LINE_GENERATED_BY_COST_SERVICE = 'COST_SERVICE_BILL_TOTAL' as const;

/** D48: the generated line's description when the bill has none. */
export const AP_BILL_GENERATED_LINE_DEFAULT_DESCRIPTION = 'Valor total da conta';

/**
 * D48: the generated line's amount, currency and text — in the bill's DOCUMENT currency, like
 * every line (the header is functional): the original amount of a foreign bill, else the total
 * (gross, as intake NFS-e lines and the D44 sum check use). Null when the bill has no positive
 * amount yet (nothing to generate). Shared by the API edit path and the worker NF-link path.
 */
export function generatedBillLineTarget<D extends { lte(value: number): boolean }>(bill: {
  totalAmount: D | null;
  originalAmount: D | null;
  currencyCode: string;
  originalCurrencyCode: string | null;
  description: string | null;
}): { amount: D; currency: string; description: string } | null {
  const foreign =
    bill.originalCurrencyCode != null && bill.originalCurrencyCode !== bill.currencyCode;
  const amount = foreign ? bill.originalAmount : bill.totalAmount;
  if (amount == null || amount.lte(0)) return null;
  return {
    amount,
    currency: foreign ? bill.originalCurrencyCode! : bill.currencyCode,
    description: bill.description?.trim() || AP_BILL_GENERATED_LINE_DEFAULT_DESCRIPTION,
  };
}

export interface CostMatchScorePartsView {
  competency: number;
  amount: number;
  dueDate: number;
  reference: number;
}

/**
 * One open provision the line could pay. `availableAmount` = open − reserved by other lines.
 * D26: an EXPECTED provision (not recognised yet; tag "esperada") has `expected` set and a
 * synthetic `entryId` = `expected:<costServiceId>:<YYYY-MM>`; selecting it (or a set holding it)
 * writes an expected selection row, which approval books as prepaid until the provision posts.
 */
export interface CostLineCandidateView {
  entryId: string;
  costServiceId: string;
  costServiceName: string;
  vendorId: string;
  currency: string;
  /** `YYYY-MM`. */
  competencyMonth: string;
  amount: string;
  openAmount: string;
  reservedByOtherLines: string;
  availableAmount: string;
  /** `YYYY-MM-DD`. */
  expectedPaymentDate: string;
  score: number;
  scoreParts: CostMatchScorePartsView;
  withinTolerance: boolean;
  /** D26: set on an expected provision ("esperada"); null/absent on a recognised one. */
  expected?: CostLineExpectedCandidateInfo | null;
}

export interface CostLineExpectedCandidateInfo {
  origin: CostExpectedSelectionOrigin;
  /** Projected vendor amount (resolved rate × scheduled revenue slice). */
  projectedAmount: string;
}

/** D26: the synthetic candidate id of an expected provision. */
export const COST_EXPECTED_CANDIDATE_PREFIX = 'expected:';

export function isExpectedCandidateId(entryId: string): boolean {
  return entryId.startsWith(COST_EXPECTED_CANDIDATE_PREFIX);
}

/** D26: `expected:<costServiceId>:<YYYY-MM>`, the id an expected candidate and its set carry. */
export function expectedCandidateId(costServiceId: string, competencyMonth: string): string {
  return `${COST_EXPECTED_CANDIDATE_PREFIX}${costServiceId}:${competencyMonth}`;
}

export interface CostMatchAllocationView {
  entryId: string;
  amountApplied: string;
}

/** A set of provisions that together fit the line within the tolerance. */
export interface CostLineSetView {
  /** Stable key of the set: its entry ids joined by `+`, oldest first. Pass `entryIds` to confirm. */
  setKey: string;
  /** D27: CROSS_SERVICE = one set per cost service of the vendor, together fitting the line. */
  kind: 'SINGLE_ENTRY' | 'MONTH_RUN' | 'CROSS_SERVICE';
  entryIds: string[];
  firstMonth: string;
  lastMonth: string;
  openTotal: string;
  score: number;
  scoreParts: CostMatchScorePartsView;
  allocations: CostMatchAllocationView[];
  varianceAmount: string;
  /**
   * D27: the cost service the set belongs to (one part of a split, or a single-service set); null
   * for a CROSS_SERVICE set, which names its services in `parts`.
   */
  costServiceId?: string | null;
  /** D27: per service of a CROSS_SERVICE set, what it applies to that service. */
  parts?: CostLineSetPartView[];
}

export interface CostLineSetPartView {
  costServiceId: string;
  entryIds: string[];
  amount: string;
}

export type CostMatchClassView = 'PRONTA' | 'AMBIGUA' | 'SEM_CANDIDATA';

/** A pending selection row of a draft line. */
export interface CostLineSelectionRowView {
  selectionId: string;
  entryId: string;
  amountApplied: string;
  source: CostMatchSource;
  selectedBy: string | null;
  selectedAt: string;
  varianceConfirmedBy: string | null;
  varianceConfirmedAt: string | null;
}

/**
 * D26 — an "expected" selection row: the line pays a provision that does not exist yet (the cost
 * service's month is scheduled or announced, not recognised). At approval it debits prepaid and
 * waits as an awaiting reservation until the cost engine posts that month's provision.
 */
export interface CostLineExpectedSelectionRowView {
  selectionId: string;
  costServiceId: string;
  costServiceName: string | null;
  /** `YYYY-MM`. */
  competencyMonth: string;
  amountApplied: string;
  /** SCHEDULE (projected from the recognition schedule) | MANUAL ("antecipar mês"). */
  origin: CostExpectedSelectionOrigin;
  source: CostMatchSource;
  selectedBy: string | null;
  selectedAt: string;
}

export const COST_EXPECTED_SELECTION_ORIGINS = ['SCHEDULE', 'MANUAL'] as const;
export type CostExpectedSelectionOrigin = (typeof COST_EXPECTED_SELECTION_ORIGINS)[number];

/** The current selection of a line with the checks a send to approval will run. */
export interface CostLineSelectionView {
  billId: string;
  lineItemId: string;
  destination: ApBillLineDestination;
  destinationSource: string | null;
  currency: string;
  lineAmount: string;
  alreadyApplied: string;
  rows: CostLineSelectionRowView[];
  /** D26: expected (not yet recognised) provisions the line pays; prepaid until they post. */
  expectedRows: CostLineExpectedSelectionRowView[];
  /** Σ rows + Σ expectedRows. */
  totalSelected: string;
  /** `line − (alreadyApplied + totalSelected)`, document currency. Negative only on overflow. */
  varianceAmount: string;
  varianceWithinTolerance: boolean;
  /** First hard check that fails today (spec order), or the variance warning, or null. */
  issueCode: string | null;
  /**
   * D43: how the line relieves its provisions. AUTOMATIC = FIFO over the open provisions of its
   * cost service (preview of what approval applies); MANUAL = the stored picks of "Ajustar".
   * Present on a COST_PROVISION line; absent on older readers.
   */
  fifo?: CostLineFifoView | null;
}

/** D43 — AUTOMATIC: FIFO; MANUAL: the line has picks made in "Ajustar" (or before D43). */
export const COST_LINE_SELECTION_MODES = ['AUTOMATIC', 'MANUAL'] as const;
export type CostLineSelectionMode = (typeof COST_LINE_SELECTION_MODES)[number];

/** D43 — one provision the automatic relief takes (oldest competência first). */
export interface CostLineFifoAllocationView {
  entryId: string;
  costServiceId: string;
  /** `YYYY-MM`. */
  competencyMonth: string;
  /** What the provision had free before this line (open − other draft bills' picks). */
  availableAmount: string;
  amountApplied: string;
  /** `availableAmount − amountApplied`: what stays open after approval. */
  remainingAmount: string;
}

/**
 * D43 — the automatic relief of a "Serviço de custo" line. Example (Royalty X, Nekt, line
 * R$ 745,00): allocations [set/2026 745 of 900, remaining 155], prepaid null.
 */
export interface CostLineFifoView {
  mode: CostLineSelectionMode;
  costServiceId: string | null;
  costServiceName: string | null;
  currency: string;
  /** Line − already applied: the amount the relief covers. */
  amount: string;
  /** Σ allocations. */
  relievedAmount: string;
  allocations: CostLineFifoAllocationView[];
  /** D23/D26: what no open provision can take, paid as prepaid for a later month. */
  prepaid: { costServiceId: string; competencyMonth: string; amount: string } | null;
  /** A reason the automatic relief cannot run (COST_LINE_FIFO_*), or null. */
  issueCode: string | null;
  /**
   * D43b — the relief per cost service of the line, in split order. One entry for a one-service
   * line; two or more when the line is split ("Dividir"). Absent on older readers.
   */
  services?: CostLineFifoServiceView[];
  /**
   * D43b — where the services came from: PREFILL = the system's choice (the exact-fit split of
   * the open provisions, else the auto-mark flagged service, else the oldest); CHOSEN = picked by
   * the user or MCP. Both are automatic (FIFO). Null for a MANUAL line.
   */
  serviceOrigin?: CostLineFifoServiceOrigin | null;
  /**
   * D45b — the excess no open provision and no later month can take, left on the expense account
   * of the line's classification at approval. Null when there is none. Absent on older readers.
   * Example: open R$ 1.000, line R$ 1.200 → { amount "200", accountLabel "4.1.01 Custo dos serviços" }.
   */
  expense?: { amount: string; accountLabel: string } | null;
  /**
   * D46-1 — the excess for a month the service already provisioned, posted to the cost service's
   * variance account at approval (never prepaid). Null when there is none. Absent on older readers.
   * Example: Sept provisioned R$ 900, line "2026-09-01 a 2026-09-30" R$ 950 → { amount "50" }.
   */
  variance?: { amount: string } | null;
  /**
   * D45a/D45b/D46-1 — alerts the approval shows before confirm (never a block): a stopped service's open
   * provisions relieved, an excess going to expense. Absent on older readers.
   */
  alerts?: CostLineFifoAlertView[];
}

/** D45a/D45b — one alert of the automatic relief, pt-BR copy filled from its params. */
export interface CostLineFifoAlertView {
  code: string;
  message: string;
  params: Record<string, string>;
}

export const COST_LINE_FIFO_SERVICE_ORIGINS = ['PREFILL', 'CHOSEN'] as const;
export type CostLineFifoServiceOrigin = (typeof COST_LINE_FIFO_SERVICE_ORIGINS)[number];

/**
 * D43b — one cost service's share of an automatic line. Example (Marca Parceira, line R$ 1.200,
 * pre-filled split): Royalty { amount 1000, relieved 1000 } + Fundo { amount 200, relieved 200 }.
 */
export interface CostLineFifoServiceView {
  costServiceId: string;
  costServiceName: string;
  /** This service's part of the line (the whole line for a one-service line). */
  amount: string;
  relievedAmount: string;
  allocations: CostLineFifoAllocationView[];
  prepaid: { costServiceId: string; competencyMonth: string; amount: string } | null;
  issueCode: string | null;
  /** D45b — this service's excess left on the classification's expense account. */
  expenseAmount?: string;
  /** D46-1 — this service's same-month excess posted to its variance account. */
  varianceAmount?: string;
}

/** GET candidates (and `cost_matches_suggest`). */
export interface CostLineCandidatesView {
  billId: string;
  lineItemId: string;
  destination: ApBillLineDestination;
  vendorId: string;
  currency: string;
  lineAmount: string;
  /** Applied by matches already posted for this line (story 48 re-match after approval). */
  alreadyApplied: string;
  /** The amount scored: `lineAmount − alreadyApplied`. */
  remainingAmount: string;
  matchClass: CostMatchClassView;
  reason: string;
  /**
   * D26: `ESPERADA` when the class rests on an expected provision (the deciding set, or the only
   * candidates, are not recognised yet); null otherwise. Screens say "(esperada)" after the reason.
   */
  reasonQualifier?: 'ESPERADA' | null;
  candidates: CostLineCandidateView[];
  sets: CostLineSetView[];
  /** Present only for PRONTA with the auto-select knob on. Never on AMBIGUA. */
  autoSelection: { allocations: CostMatchAllocationView[]; varianceAmount: string } | null;
  otherCurrencyOpenEntryCount: number;
  selection: CostLineSelectionView;
  /**
   * D27: the line-level "Serviço de custo" choice. `available` only when the bill's vendor has two
   * or more ACTIVE cost services in the entity on the bill date; absent on older readers.
   */
  serviceChoice?: CostLineServiceChoiceView;
  /** D27: the cost service the candidates were filtered to (query filter or the line's choice). */
  costServiceFilter?: string | null;
}

/** D27 — "Todos" (every service, the default) | one service | "Dividir" (an amount per service). */
export const COST_LINE_SERVICE_CHOICE_MODES = ['ALL', 'ONE', 'SPLIT'] as const;
export type CostLineServiceChoiceMode = (typeof COST_LINE_SERVICE_CHOICE_MODES)[number];

export interface CostLineServiceOptionView {
  costServiceId: string;
  name: string;
  /** D43b: the template the service was created from (`Royalty`). Absent on older readers. */
  templateName?: string;
  autoMarkVendorBills: boolean;
  /**
   * D43b: what the service's open provisions still have free for this line (posted, open, in the
   * line currency, minus other draft bills' picks). Absent on older readers.
   */
  openAmount?: string;
}

export interface CostLineServicePartView {
  costServiceId: string;
  costServiceName: string;
  /** The part of the line this service pays (SPLIT); null for ONE. */
  amount: string | null;
  source: CostMatchSource;
  /** SPLIT: the part scored inside its own service. */
  matchClass?: CostMatchClassView;
  reason?: string;
  /** SPLIT: what the line's selection gives this service today. */
  selectedAmount?: string;
}

export interface CostLineServiceChoiceView {
  available: boolean;
  mode: CostLineServiceChoiceMode;
  /** The vendor's ACTIVE cost services in the entity on the bill date, oldest first. */
  services: CostLineServiceOptionView[];
  parts: CostLineServicePartView[];
}

/** D27 — what a service-choice write accepts (API body, MCP tool). */
export type CostLineServiceChoiceInput =
  | { mode: 'ALL' }
  | { mode: 'ONE'; costServiceId: string }
  | { mode: 'SPLIT'; parts: { costServiceId: string; amount: string }[] };

/**
 * ALC-4476 — the bill read's provision block (`costProvision` on GET /ap/bills/:id and
 * `ap_bills_get`): "Baixa de provisão: R$ 990,00 de R$ 1.000,00" and every match per line, live
 * and undone. Amounts are decimal strings; `*Functional` are signed (+ debit, − credit).
 */
export interface BillCostProvisionMatchView {
  matchId: string;
  entryId: string;
  bookId: string;
  journalId: string | null;
  kind: string;
  source: string;
  currency: string;
  amountApplied: string;
  varianceAmount: string;
  openReduction: string;
  liabilityFunctional: string;
  costVarianceFunctional: string;
  fxVarianceFunctional: string;
  matchedAt: string;
  matchedBy: string | null;
  undoneAt: string | null;
  undoneBy: string | null;
  undoReason: string | null;
  undoJournalId: string | null;
  /**
   * The provision this match pays, named on the bill read: the AP analyst who sees the bill cannot
   * read the Cost Services list. Null only for a provision row the read could not find.
   */
  costServiceName: string | null;
  /** `YYYY-MM` of the paid provision's competency date; null with `costServiceName`. */
  competencyMonth: string | null;
  /**
   * `YYYY-MM-DD` of the match's journal: for a PREPAID match (D23/D26) the reclass date that moved
   * the prepaid onto the provision ("Baixada em 31/10/2026"); null when the journal is unknown.
   */
  journalDate?: string | null;
  /** D26: the awaiting reservation a PREPAID match consumed; null otherwise. */
  reservationId?: string | null;
}

/**
 * D26 — a part of the line booked as prepaid for an expected provision (one book): "Aguardando
 * provisão (antecipado) · 10/2026 · R$ 1.000,00" while AWAITING, "Baixada em 31/10/2026" once the
 * provision consumed it. Amounts are decimal strings in the bill's currency.
 */
export interface BillCostAwaitingReservationView {
  reservationId: string;
  bookId: string;
  costServiceId: string;
  costServiceName: string | null;
  /** `YYYY-MM`. */
  competencyMonth: string;
  /** SCHEDULE | MANUAL. */
  origin: string;
  /** AWAITING | CONSUMED | CANCELLED | CLOSED | VOIDED. */
  status: string;
  currency: string;
  amount: string;
  consumedAmount: string;
  releasedAmount: string;
  /** Still prepaid: amount − consumed − released. */
  remainingAmount: string;
  /** `YYYY-MM-DD` of the bill journal that debited prepaid. */
  prepaidDate: string | null;
  /** `YYYY-MM-DD` of the latest consumption (reclass) journal; null until a provision consumes it. */
  consumedOn: string | null;
  createdAt: string;
  releasedAt: string | null;
  releaseReason: string | null;
}

export interface BillCostProvisionLineView {
  lineItemId: string;
  lineNumber: number;
  destination: string;
  lineAmount: string;
  /** Live applied units of the line (one book). */
  appliedAmount: string;
  matches: BillCostProvisionMatchView[];
  /** D26: the line's awaiting reservations (read book), oldest first. */
  awaiting?: BillCostAwaitingReservationView[];
  /**
   * D27: the name of the cost service whose vendor flag stamped the line (`COST_SERVICE_FLAG:<id>`
   * source), so a read-only bill can say "Marcado automaticamente (serviço de custo «X»)". Null
   * when another source set the destination.
   */
  flaggedCostServiceName?: string | null;
  /**
   * D43: on a COST_PROVISION line of a bill still in draft, the automatic relief preview (or the
   * MANUAL mode of a line adjusted by hand). Null once the bill left draft.
   */
  fifo?: CostLineFifoView | null;
}

export interface BillCostProvisionSummary {
  currency: string;
  appliedTotal: string;
  /** Document amount of the lines that pay (or once paid) provisions. */
  lineTotal: string;
  /** Document amount of every line of the bill: the "de R$Y" of the label (story 50). */
  billTotal: string;
  /** pt-BR: "Baixa de provisão: R$ 40.000,00 de R$ 52.000,00" (applied of the bill's gross). */
  label: string;
  lines: BillCostProvisionLineView[];
}
